import Header from '@/components/Header';
import { useEffect, useState } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { AlertsMap } from '@/components/AlertsMap';
import { Route as RouteIcon, ShieldAlert, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Restrictions() {
  const { user } = useAuth();
  const business = user?.role === 'admin' || user?.plan === 'business';
  const [items, setItems] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [routeId, setRouteId] = useState('');
  const [report, setReport] = useState(null);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!business) { setLocked(true); return; }
    api.get('/restrictions?limit=3000').then((r) => setItems(r.data)).catch((e) => { if (e?.response?.status === 403) setLocked(true); });
    api.get('/routes').then((r) => setRoutes(r.data)).catch(() => {});
  }, [business]);

  const runReport = async (id) => {
    setRouteId(id); setReport(null);
    if (!id) return;
    try { const { data } = await api.get(`/routes/${id}/restrictions`); setReport(data); } catch {}
  };

  if (locked) {
    return (
      <div className="min-h-screen"><Header />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="restrictions-locked">
          <ShieldAlert className="w-12 h-12 mx-auto text-orange-500 mb-4" />
          <h1 className="font-display font-black text-3xl mb-2">Freight Restrictions — Business plan</h1>
          <p className="text-zinc-600 mb-6">Bridge crossing restrictions and route clearance reports for oversize / heavy loads are part of the Business (Transport &amp; Load Pilots) plan.</p>
          <Link to="/settings" className="btn btn-primary">View plans</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen"><Header />
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="flex items-center gap-3 mb-1"><Truck className="w-6 h-6 text-teal-600" /><h1 className="font-display font-black text-3xl md:text-4xl">Freight Restrictions</h1></div>
        <p className="text-zinc-600 mb-6">NZTA freight layers — crane-bridge crossings, 50MAX &amp; HPMV (58t) approved routes for heavy &amp; oversize loads. <strong>{items.length}</strong> entries mapped.</p>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <AlertsMap alerts={[]} restrictions={items} height={440} />
          </div>
          <div className="card" data-testid="route-report">
            <h3 className="font-display font-bold text-lg flex items-center gap-2 mb-3"><RouteIcon className="w-4 h-4 text-teal-600" /> Restrictions on my route</h3>
            {routes.length === 0 ? (
              <p className="text-sm text-zinc-500">Create a route in Settings to check bridge restrictions along it.</p>
            ) : (
              <>
                <select className="input mb-4" value={routeId} onChange={(e) => runReport(e.target.value)} data-testid="route-report-select">
                  <option value="">Select a route…</option>
                  {routes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                {report && (
                  <div>
                    <div className="mb-3 p-2.5 rounded-lg bg-orange-50 border border-orange-200 text-xs text-orange-900" data-testid="route-most-restrictive">
                      <strong>Most restrictive on this route:</strong> {report.do_not_cross > 0 ? `${report.do_not_cross} “DO NOT CROSS” bridge${report.do_not_cross > 1 ? 's' : ''}` : 'no do-not-cross bridges'}{report.min_axle != null ? ` · lowest axle rating ${report.min_axle}` : ''}.
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 p-3 rounded-lg bg-zinc-50 text-center"><div className="font-display font-black text-2xl">{report.count}</div><div className="text-[10px] uppercase tracking-wider text-zinc-500">restrictions</div></div>
                      <div className="flex-1 p-3 rounded-lg bg-red-50 text-center"><div className="font-display font-black text-2xl text-red-600">{report.do_not_cross}</div><div className="text-[10px] uppercase tracking-wider text-red-700">do not cross</div></div>
                    </div>
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                      {report.restrictions.map((r) => (
                        <div key={r.id} className="p-2 rounded border border-zinc-100 text-xs" data-testid={`report-item-${r.id}`}>
                          <div className="font-semibold">{r.name}</div>
                          <div className="text-zinc-500">{r.highway} · {r.condition || '—'}{r.axle ? ` · axle ${r.axle}` : ''}</div>
                        </div>
                      ))}
                      {report.restrictions.length === 0 && <p className="text-sm text-teal-700">No known bridge restrictions on this route. ✅</p>}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="card mt-6 p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500"><tr>
              <th className="text-left px-4 py-3">Type</th><th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Highway</th><th className="text-left px-4 py-3">Condition</th><th className="text-left px-4 py-3">Axle</th><th className="text-left px-4 py-3">Region</th></tr></thead>
            <tbody>
              {items.slice(0, 200).map((r) => (
                <tr key={r.id} className="border-t border-zinc-100" data-testid={`restriction-row-${r.id}`}>
                  <td className="px-4 py-2.5"><span className={`chip text-[10px] uppercase ${r.type === 'hpmv' ? 'chip-outline-teal' : r.type === '50max' ? 'chip-outline-orange' : 'chip-red'}`}>{r.type || 'bridge'}</span></td>
                  <td className="px-4 py-2.5 font-medium">{r.name}</td>
                  <td className="px-4 py-2.5">{r.highway || r.highway_raw || '—'}{r.island ? ` · ${r.island === 'north' ? 'N' : 'S'}` : ''}</td>
                  <td className="px-4 py-2.5">{(r.condition || '').toUpperCase().startsWith('DO NOT') ? <span className="chip chip-red text-[10px]">{r.condition}</span> : (r.condition || '—')}</td>
                  <td className="px-4 py-2.5">{r.axle ?? '—'}</td>
                  <td className="px-4 py-2.5 text-zinc-500">{r.region || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
